import { z } from "zod";
import { localDateSchema, moneySchema, uuidSchema } from "../common/primitives.js";
/**
 * Nakit akış projeksiyonu (Prompt 11 §7; özellik bayrağı `EXPENSES_BUDGET`).
 *
 * Projeksiyon SAF bir hesaptır (`cashflow/domain/projection.ts`); burada yalnızca girdi/çıktı sözleşmeleri var.
 * Senaryo yalnızca GİRİŞLERİ etkiler — kötümser senaryo borçları ertelemez. Hafta sonu / tatil kaydırması YOK.
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const queryBoolean = z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true");
export const CASHFLOW_SCENARIOS = ["OPTIMISTIC", "BASE", "PESSIMISTIC"];
export const cashflowScenarioSchema = z.enum(CASHFLOW_SCENARIOS);
export const CASHFLOW_SCENARIO_LABELS = {
    OPTIMISTIC: "İyimser",
    BASE: "Gerçekçi",
    PESSIMISTIC: "Kötümser",
};
export const CASHFLOW_ITEM_KINDS = [
    "RECEIVABLE",
    "CHEQUE_RECEIVED",
    "POS_SETTLEMENT",
    "OTHER_INFLOW",
    "PAYABLE",
    "CHEQUE_ISSUED",
    "DBS",
    "LOAN_INSTALLMENT",
    "RECURRING_EXPENSE",
    "TAX",
    "OTHER_OUTFLOW",
];
export const cashflowItemKindSchema = z.enum(CASHFLOW_ITEM_KINDS);
export const CASHFLOW_ITEM_KIND_LABELS = {
    RECEIVABLE: "Cari alacak",
    CHEQUE_RECEIVED: "Alınan çek / senet",
    POS_SETTLEMENT: "POS yatışı",
    OTHER_INFLOW: "Diğer tahsilat",
    PAYABLE: "Cari borç",
    CHEQUE_ISSUED: "Verilen çek / senet",
    DBS: "DBS ödemesi",
    LOAN_INSTALLMENT: "Kredi taksiti",
    RECURRING_EXPENSE: "Tekrarlayan gider",
    TAX: "Vergi / SGK",
    OTHER_OUTFLOW: "Diğer ödeme",
};
/**
 * Kalemin geldiği KAYNAK BELGE türü (`apps/api/src/cashflow/cashflow-sources.ts` sağlayıcılarının ürettiği
 * değerlerin tamamı). İlk altısı ödeme planlamasının `PAYMENT_SOURCE_TYPES`'ıyla AYNIDIR — `inPaymentBatch`
 * eşleştirmesi bunlarla yapılır. Yeni bir kaynak modülü eklenince bu listeye de eklenir.
 */
export const CASHFLOW_SOURCE_TYPES = [
    "OPEN_ITEM",
    "INSTRUMENT",
    "DBS_ITEM",
    "LOAN_INSTALLMENT",
    "TAX_OBLIGATION",
    "COMPANY_CARD",
    "POS_BATCH",
    "RECURRING_EXPENSE",
    "FUEL_SALES_FORECAST",
];
export const cashflowSourceTypeSchema = z.enum(CASHFLOW_SOURCE_TYPES);
export const CASHFLOW_SOURCE_TYPE_LABELS = {
    OPEN_ITEM: "Cari açık kalemi",
    INSTRUMENT: "Çek / senet",
    DBS_ITEM: "DBS kalemi",
    LOAN_INSTALLMENT: "Kredi taksiti",
    TAX_OBLIGATION: "Vergi / SGK yükümlülüğü",
    COMPANY_CARD: "Şirket kredi kartı",
    POS_BATCH: "POS yatışı",
    RECURRING_EXPENSE: "Tekrarlayan gider",
    FUEL_SALES_FORECAST: "Akaryakıt satış tahmini",
};
export const CASHFLOW_VIEWS = ["DAILY", "WEEKLY"];
export const cashflowViewSchema = z.enum(CASHFLOW_VIEWS);
export const MAX_CASHFLOW_HORIZON_DAYS = 90;
// ── Ayarlar ──────────────────────────────────────────────────────────────────
export const cashflowScenarioParamsSchema = z.object({
    receivableDelayDays: z
        .number()
        .int()
        .min(0)
        .max(120)
        .describe("Cari alacaklar vadesinden kaç gün SONRA tahsil edilir"),
    chequeBounceBasisPoints: z
        .number()
        .int()
        .min(0)
        .max(10_000)
        .describe("Alınan çeklerin karşılıksız oranı (baz puan: 250 = %2,5)"),
    posDelayDays: z.number().int().min(0).max(120).describe("POS yatışı valörden kaç gün gecikir"),
});
/** Çıktıda üç senaryo da DOLUDUR (kaydedilmemiş anahtarlar varsayılandan tamamlanır). */
const scenarioMapSchema = z.object({
    OPTIMISTIC: cashflowScenarioParamsSchema,
    BASE: cashflowScenarioParamsSchema,
    PESSIMISTIC: cashflowScenarioParamsSchema,
});
/** Girdide yalnızca DEĞİŞTİRİLEN senaryolar gönderilir. */
const scenarioPatchSchema = scenarioMapSchema.partial();
export const cashflowSettingsSchema = z.object({
    companyId: uuidSchema,
    horizonDays: z.number().int(),
    alertScenario: cashflowScenarioSchema,
    alertHorizonDays: z.number().int(),
    /** Üç senaryonun da parametreleri (kaydedilmemiş anahtarlar varsayılandan gelir). */
    scenarios: scenarioMapSchema,
    version: versionSchema,
});
export const updateCashflowSettingsSchema = z.object({
    horizonDays: z.number().int().min(7).max(MAX_CASHFLOW_HORIZON_DAYS).optional(),
    alertScenario: cashflowScenarioSchema.optional(),
    alertHorizonDays: z.number().int().min(1).max(MAX_CASHFLOW_HORIZON_DAYS).optional(),
    scenarios: scenarioPatchSchema.optional(),
    version: versionSchema,
});
// ── Projeksiyon ──────────────────────────────────────────────────────────────
export const cashflowProjectionQuerySchema = z.object({
    companyId: uuidSchema,
    scenario: cashflowScenarioSchema.default("BASE"),
    horizonDays: z.coerce.number().int().min(1).max(MAX_CASHFLOW_HORIZON_DAYS).optional(),
    view: cashflowViewSchema.default("DAILY"),
    stationId: uuidSchema.optional().describe("İstasyon görünümü: yalnızca o istasyonun kalemleri"),
    includeItems: queryBoolean.describe("Günlerin kalem dökümü de dönsün mü"),
});
export const cashflowProjectedItemSchema = z.object({
    date: localDateSchema.describe("Kalemin gerçek vadesi"),
    projectedDate: localDateSchema.describe("Senaryo uygulandıktan sonra düştüğü gün"),
    kind: cashflowItemKindSchema,
    amount: moneySchema.describe("Kuruş; HER ZAMAN pozitif — yönü tür belirler"),
    projectedAmount: moneySchema,
    signedAmount: moneySchema.describe("± işaretli etki"),
    overdue: z.boolean(),
    stationId: uuidSchema.nullable(),
    sourceType: cashflowSourceTypeSchema,
    sourceId: uuidSchema,
    /** Kaynak biliyorsa kalemin carisi (cari kartına bağlantı); bilmiyorsa null. */
    counterpartyId: uuidSchema.nullable(),
    label: z.string().describe("Kişisel veri / IBAN içermez"),
    /** Kaynak etkin bir ödeme partisinde bekliyor mu (bilgi; projeksiyonu DEĞİŞTİRMEZ). */
    inPaymentBatch: z.boolean(),
});
export const cashflowDaySchema = z.object({
    date: localDateSchema,
    inflow: moneySchema,
    outflow: moneySchema,
    net: moneySchema,
    closingBalance: moneySchema,
    available: moneySchema.describe("Gün sonu bakiye + KMH limiti"),
    shortfall: z.boolean(),
    items: z.array(cashflowProjectedItemSchema),
});
export const cashflowWeekSchema = z.object({
    weekStart: localDateSchema,
    from: localDateSchema,
    to: localDateSchema,
    inflow: moneySchema,
    outflow: moneySchema,
    net: moneySchema,
    closingBalance: moneySchema,
    minAvailable: moneySchema,
    shortfall: z.boolean(),
});
export const cashflowProjectionSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    scenario: cashflowScenarioSchema,
    view: cashflowViewSchema,
    from: localDateSchema,
    to: localDateSchema,
    openingBalance: moneySchema,
    overdraftLimit: moneySchema,
    days: z.array(cashflowDaySchema),
    weeks: z.array(cashflowWeekSchema),
    shortfallDays: z.array(localDateSchema),
    lowestAvailable: z.object({ date: localDateSchema, amount: moneySchema }),
    totals: z.object({
        inflow: moneySchema,
        outflow: moneySchema,
        net: moneySchema,
        closingBalance: moneySchema,
    }),
    beyondHorizon: z.number().int().describe("Ufkun dışında kaldığı için girmeyen kalem sayısı"),
    /** Tutarı henüz girilmemiş, bu yüzden projeksiyona GİRMEYEN vergi yükümlülüğü sayısı. */
    unknownAmountObligations: z.number().int(),
    /** Kullanıcıya gösterilecek notlar (ör. istasyon görünümünde banka bakiyesi taşınmaz). */
    notes: z.array(z.string()),
});
export const cashflowCompareQuerySchema = z.object({
    companyId: uuidSchema,
    horizonDays: z.coerce.number().int().min(1).max(MAX_CASHFLOW_HORIZON_DAYS).optional(),
    stationId: uuidSchema.optional(),
});
export const cashflowScenarioSummarySchema = z.object({
    scenario: cashflowScenarioSchema,
    closingBalance: moneySchema,
    lowestAvailable: moneySchema,
    lowestDate: localDateSchema,
    shortfallDayCount: z.number().int(),
    firstShortfallDate: localDateSchema.nullable(),
    totals: z.object({ inflow: moneySchema, outflow: moneySchema, net: moneySchema }),
});
export const cashflowCompareSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    from: localDateSchema,
    to: localDateSchema,
    openingBalance: moneySchema,
    overdraftLimit: moneySchema,
    scenarios: z.array(cashflowScenarioSummarySchema),
});
export const cashflowItemsQuerySchema = z.object({
    companyId: uuidSchema,
    date: localDateSchema.describe("Projeksiyondaki gün (senaryo uygulanmış tarih)"),
    scenario: cashflowScenarioSchema.default("BASE"),
    horizonDays: z.coerce.number().int().min(1).max(MAX_CASHFLOW_HORIZON_DAYS).optional(),
    stationId: uuidSchema.optional(),
});
export const cashflowItemListSchema = z.object({
    date: localDateSchema,
    inflow: moneySchema,
    outflow: moneySchema,
    items: z.array(cashflowProjectedItemSchema),
});
export const cashflowAlertRunResultSchema = z.object({
    companiesChecked: z.number().int(),
    alertsRaised: z.number().int(),
    failed: z.array(z.object({ companyId: uuidSchema, error: z.string() })),
});
//# sourceMappingURL=cashflow.js.map